/**
 * This device's copy of a view's data (docs/views.md#2-data): one IndexedDB database with one store of JSON values by
 * key, so a view opens from the device at once and a write made offline waits there for the connection. Where
 * IndexedDB is missing, slow, or refused, nothing is kept and nothing fails.
 */
const NAME = "coast-device-v1";
const STORE = "kept";
/** How long opening the database may take before this device goes on without it. */
const OPEN_MS = 4_000;
let opening: Promise<IDBDatabase | null> | null = null;

function open(): Promise<IDBDatabase | null> {
  opening ??= new Promise<IDBDatabase | null>((resolve) => {
    let settled = false;
    const done = (db: IDBDatabase | null) => {
      if (!settled) {
        settled = true;
        resolve(db);
      }
    };
    setTimeout(() => done(null), OPEN_MS);
    try {
      const request = indexedDB.open(NAME, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE);
      };
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => db.close();
        done(db);
      };
      request.onerror = () => done(null);
      request.onblocked = () => done(null);
    } catch {
      done(null);
    }
  });
  return opening;
}

/** A request's result, or null when the store refused it. */
function result<T>(db: IDBDatabase, mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>) {
  return new Promise<T | null>((resolve) => {
    try {
      const request = work(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export async function readKept<T>(key: string): Promise<T | null> {
  const db = await open();
  return db ? ((await result(db, "readonly", (store) => store.get(key))) as T | null) : null;
}
export async function keep(key: string, value: unknown): Promise<void> {
  const db = await open();
  if (db) await result(db, "readwrite", (store) => store.put(value, key));
}
/** The keys that start with `prefix`. */
const under = (prefix: string) => IDBKeyRange.bound(prefix, `${prefix}￿`);
/** Every value kept under a key that starts with `prefix`. */
export async function readKeptUnder<T>(prefix: string): Promise<T[]> {
  const db = await open();
  if (!db) return [];
  return ((await result(db, "readonly", (store) => store.getAll(under(prefix)))) ?? []) as T[];
}
export async function forgetKept(key: string): Promise<void> {
  const db = await open();
  if (db) await result(db, "readwrite", (store) => store.delete(key));
}
/** The keys kept under `prefix`, or null when this device keeps nothing, so a caller can tell none from no store. */
export async function keptKeysUnder(prefix: string): Promise<string[] | null> {
  const db = await open();
  if (!db) return null;
  const keys = await result(db, "readonly", (store) => store.getAllKeys(under(prefix)));
  return keys ? keys.map(String) : null;
}
export async function forgetKeptUnder(prefix: string): Promise<void> {
  const db = await open();
  if (db) await result(db, "readwrite", (store) => store.delete(under(prefix)));
}
